/**
 * Response utilities for the API handler
 */

import type { ApiResponse } from './types';

/**
 * Create a successful API response
 */
export function createApiResponse(
  data: any,
  requestId: string,
  status: number = 200
): Response {
  const response: ApiResponse = {
    data,
    meta: {
      requestId,
      timestamp: new Date().toISOString(),
      version: '1.1.0',
    },
  };

  return new Response(JSON.stringify(response), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': requestId,
      'X-API-Version': '1.1.0',
    },
  });
}

/**
 * Create an error response
 */
export function createErrorResponse(
  code: string,
  status: number,
  details: {
    message: string;
    cause?: string;
    solution?: string;
    docs?: string;
  },
  requestId: string,
  debugInfo?: any
): Response {
  const response: ApiResponse = {
    error: {
      code,
      message: details.message,
      cause: details.cause,
      solution: details.solution,
      docs: details.docs,
    },
    meta: {
      requestId,
      timestamp: new Date().toISOString(),
      version: '1.1.0',
    },
  };

  // Add debug info in development
  if (debugInfo && process.env.NODE_ENV === 'development') {
    (response.error as any).debug = {
      error: debugInfo instanceof Error ? {
        name: debugInfo.name,
        message: debugInfo.message,
        stack: debugInfo.stack,
      } : debugInfo,
    };
  }

  return new Response(JSON.stringify(response), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': requestId,
      'X-API-Version': '1.1.0',
    },
  });
}

/**
 * Create a streaming response for real-time data
 */
export function createStreamResponse(
  requestId: string,
  onStream: (send: (data: any) => void) => Promise<void>
): Response {
  const encoder = new TextEncoder();

  const stream = new ReadableStream({
    async start(controller) {
      const send = (data: any) => {
        const message = `data: ${JSON.stringify({
          ...data,
          meta: {
            requestId,
            timestamp: new Date().toISOString(),
          },
        })}\n\n`;
        controller.enqueue(encoder.encode(message));
      };

      try {
        await onStream(send);
      } catch (error) {
        const errorMessage = `data: ${JSON.stringify({
          type: 'error',
          error: {
            code: 'STREAM_ERROR',
            message: 'Stream error occurred',
            cause: error instanceof Error ? error.message : String(error),
          },
          meta: {
            requestId,
            timestamp: new Date().toISOString(),
          },
        })}\n\n`;
        controller.enqueue(encoder.encode(errorMessage));
      } finally {
        controller.close();
      }
    },
  });

  return new Response(stream, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
      'X-Request-ID': requestId,
    },
  });
}